<!-- eslint-disable @angular-eslint/template/interactive-supports-focus -->
<!-- eslint-disable @angular-eslint/template/click-events-have-key-events -->
<div class="question-wrapper" [class.view-mode]="viewAnswerOnly">
  <!-- Question Text -->
  <p class="question-text" [class.required]="question.required && !(response.answer.length > 0)">
    {{ question.text }}
    <span class="required-mark" *ngIf="!viewAnswerOnly && question.required">*</span>
  </p>

  <!-- Question Image -->
  <div class="question-image" *ngIf="question.imageUrls">
    <img [src]="question.imageUrls.path" [alt]="question.imageUrls.caption" />
    <p class="image-caption" *ngIf="question.imageUrls.caption">{{ question.imageUrls.caption }}</p>
  </div>

  <!-- Input Types -->
  <ng-container [ngSwitch]="question.answerDisplay?.type || question.type">
    <!-- RADIO BUTTONS (Single Choice) -->
    <div *ngSwitchCase="InputType.RADIO" class="radio-options">
      <ng-container *ngFor="let choice of choices">
        <div
          *ngIf="!viewAnswerOnly || isChoiceSelected(choice.code)"
          class="option-card"
          [class.selected]="isChoiceSelected(choice.code)"
          (click)="!viewAnswerOnly && selectSingleChoice(choice.code)"
        >
          <div class="option-radio">
            <div class="radio-outer">
              <div class="radio-inner" *ngIf="isChoiceSelected(choice.code)"></div>
            </div>
          </div>
          <span class="option-text">{{ choice.display }}</span>
        </div>
      </ng-container>
    </div>

    <!-- CHECKBOXES (Multiple Choice) -->
    <div *ngSwitchCase="InputType.CHECKBOXES" class="checkbox-group">
      <ng-container *ngFor="let choice of choices">
        <label
          *ngIf="!viewAnswerOnly || isChoiceSelected(choice.code)"
          class="checkbox-item"
          [class.selected]="isChoiceSelected(choice.code)"
        >
          <input
            type="checkbox"
            [checked]="isChoiceSelected(choice.code)"
            (change)="!viewAnswerOnly && toggleMultipleChoice(choice.code)"
          />
          <span class="checkbox-custom">
            <svg
              *ngIf="isChoiceSelected(choice.code)"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </span>

          <span class="checkbox-label">{{ choice.display }}</span>
        </label>
      </ng-container>
    </div>

    <!-- DROPDOWN (Select) -->
    <div *ngSwitchCase="InputType.DROPDOWN" class="dropdown-wrapper">
      <mat-form-field appearance="outline" class="full-width">
        <mat-label *ngIf="!viewAnswerOnly">
          {{ "page.questionnaire.selectOption" | translate }}
        </mat-label>

        <mat-select
          [disabled]="viewAnswerOnly"
          [value]="response.answer[0].valueCoding.code || question.answerDisplay?.default"
          (selectionChange)="!viewAnswerOnly && selectSingleChoice($event.value)"
        >
          <ng-container *ngFor="let choice of choices">
            <mat-option *ngIf="!viewAnswerOnly || isChoiceSelected(choice.code)" [value]="choice.code">
              {{ choice.display }}
            </mat-option>
          </ng-container>
        </mat-select>
      </mat-form-field>
    </div>

    <!-- RANGE/SCALE -->
    <div *ngSwitchCase="InputType.RANGE" class="scale-wrapper">
      <div class="scale-display" *ngIf="question.answerDisplay?.displayIsShown">
        <h5>{{ getRangeValue() }}</h5>
      </div>

      <div class="scale-input">
        <span class="scale-min">{{ question.answerDisplay?.min }}</span>
        <mat-slider
          [min]="question.answerDisplay?.min ?? 0"
          [max]="question.answerDisplay?.max ?? 10"
          [step]="question.answerDisplay?.step || 1"
          [disabled]="viewAnswerOnly"
          thumbLabel
        >
          <input matSliderThumb [value]="getRangeValue()" (valueChange)="!viewAnswerOnly && onRangeChange($event)" />
        </mat-slider>

        <span class="scale-max">{{ question.answerDisplay?.max }}</span>
      </div>
    </div>

    <!-- TEXT INPUT (String) -->
    <div *ngSwitchCase="InputType.STRING">
      <mat-form-field appearance="outline" class="full-width">
        <mat-label *ngIf="!viewAnswerOnly">
          {{ "page.questionnaire.yourAnswer" | translate }}
        </mat-label>

        <input
          matInput
          [readonly]="viewAnswerOnly"
          [value]="response.answer[0].valueCoding.code || question.answerDisplay?.default || ''"
          (change)="!viewAnswerOnly && onTextChange($any($event.target).value)"
          [placeholder]="question.answerDisplay?.placeholder || ''"
        />
      </mat-form-field>
    </div>

    <!-- TEXTAREA (Text) -->
    <div *ngSwitchCase="InputType.TEXT">
      <mat-form-field appearance="outline" class="full-width">
        <mat-label *ngIf="!viewAnswerOnly">
          {{ "page.questionnaire.yourAnswer" | translate }}
        </mat-label>

        <textarea
          matInput
          rows="3"
          [readonly]="viewAnswerOnly"
          [value]="response.answer[0].valueCoding.code || question.answerDisplay?.default || ''"
          (change)="!viewAnswerOnly && onTextChange($any($event.target).value)"
          [placeholder]="question.answerDisplay?.placeholder || ''"
        >
        </textarea>
      </mat-form-field>
    </div>

    <!-- TEXTFIELD (Multiline) -->
    <div *ngSwitchCase="InputType.TEXTFIELD">
      <mat-form-field appearance="outline" class="full-width">
        <mat-label *ngIf="!viewAnswerOnly">
          {{ "page.questionnaire.yourAnswer" | translate }}
        </mat-label>

        <textarea
          matInput
          cdkTextareaAutosize
          cdkAutosizeMinRows="3"
          cdkAutosizeMaxRows="10"
          [readonly]="viewAnswerOnly"
          [value]="response.answer[0].valueCoding.code || question.answerDisplay?.default || ''"
          (change)="!viewAnswerOnly && onTextChange($any($event.target).value)"
          [placeholder]="question.answerDisplay?.placeholder || ''"
        >
        </textarea>
      </mat-form-field>
    </div>

    <!-- NUMBER INPUT -->
    <div *ngSwitchCase="InputType.NUMBER">
      <div class="number-wrapper">
        <mat-form-field appearance="outline" [class.with-unit]="question.options.reference">
          <mat-label *ngIf="!viewAnswerOnly">
            {{ "page.questionnaire.yourAnswer" | translate }}
          </mat-label>

          <input
            matInput
            type="number"
            [readonly]="viewAnswerOnly"
            [value]="response.answer[0].valueCoding.code || question.answerDisplay?.default || ''"
            [min]="question.answerDisplay?.min"
            [max]="question.answerDisplay?.max"
            [step]="question.answerDisplay?.step || 1"
            (change)="!viewAnswerOnly && onNumberChange($any($event.target).value)"
          />
        </mat-form-field>

        <!-- Unit selector if available -->
        <mat-form-field appearance="outline" class="unit-select" *ngIf="question.options.reference && choices.length > 0">
          <mat-select
            [disabled]="viewAnswerOnly"
            [value]="response.answer[1].valueCoding.code || choices[0].code"
            (selectionChange)="!viewAnswerOnly && onNumberChange($event.value, true, 1)"
          >
            <mat-option *ngFor="let choice of choices" [value]="choice.code">
              {{ choice.display }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>
    </div>

    <!-- DECIMAL INPUT -->
    <div *ngSwitchCase="InputType.DECIMAL">
      <mat-form-field appearance="outline" class="full-width">
        <mat-label *ngIf="!viewAnswerOnly">
          {{ "page.questionnaire.yourAnswer" | translate }}
        </mat-label>

        <input
          matInput
          type="number"
          [readonly]="viewAnswerOnly"
          [value]="response.answer[0].valueCoding.code || question.answerDisplay?.default || ''"
          [min]="question.answerDisplay?.min"
          [max]="question.answerDisplay?.max"
          [step]="question.answerDisplay?.step || 0.01"
          (change)="!viewAnswerOnly && onNumberChange($any($event.target).value)"
        />
      </mat-form-field>
    </div>

    <!-- DATE INPUT -->
    <div *ngSwitchCase="InputType.DATE">
      <mat-form-field appearance="outline" class="full-width">
        <mat-label *ngIf="!viewAnswerOnly">
          {{ "page.questionnaire.selectDate" | translate }}
        </mat-label>

        <input
          matInput
          [readonly]="viewAnswerOnly"
          [matDatepicker]="picker"
          [value]="getDateValue()"
          [min]="question.answerDisplay?.min"
          [max]="question.answerDisplay?.max"
          (dateChange)="!viewAnswerOnly && onDateChange($event.value)"
        />

        <mat-hint *ngIf="question.answerDisplay?.placeholder">
          {{ question.answerDisplay?.placeholder }}
        </mat-hint>

        <mat-datepicker-toggle *ngIf="!viewAnswerOnly" matSuffix [for]="picker"> </mat-datepicker-toggle>

        <mat-datepicker #picker></mat-datepicker>
      </mat-form-field>
    </div>

    <!-- RATING INPUT -->
    <div *ngSwitchCase="InputType.RATING" class="rating-wrapper">
      <div class="star-rating">
        <span
          *ngFor="let star of ratingStars; let i = index"
          class="star"
          [class.filled]="i < ratingValue"
          (click)="!viewAnswerOnly && onRatingChange(i + 1)"
        >
          ★
        </span>
      </div>

      <p class="rating-value">{{ ratingValue }} / {{ question.answerDisplay?.max || 5 }}</p>
    </div>

    <!-- CHOICE (Generic - defaults to radio) -->
    <div *ngSwitchCase="InputType.CHOICE" class="radio-options">
      <ng-container *ngFor="let choice of choices">
        <div
          *ngIf="!viewAnswerOnly || isChoiceSelected(choice.code)"
          class="option-card"
          [class.selected]="isChoiceSelected(choice.code)"
          (click)="!viewAnswerOnly && selectSingleChoice(choice.code)"
        >
          <div class="option-radio">
            <div class="radio-outer">
              <div class="radio-inner" *ngIf="isChoiceSelected(choice.code)"></div>
            </div>
          </div>

          <span class="option-text">{{ choice.display }}</span>
        </div>
      </ng-container>
    </div>
  </ng-container>
</div>
